import { CardStatusEnum, ProCard, RepCard, MyProjectsIntroduction } from '@platform/client/schemas';
import { PillBadgeColors, ButtonVariants } from '@platform/components';
import { ReactNode } from 'react';
import { NumberTag } from '@platform/components';
import { navigate } from 'wouter/use-browser-location';

const USER_SENT_INTRO_HEADER = (
  <div>
    Introduction sent! We will let you know within{' '}
    <NumberTag size='sm' variant='badge' leadNumber={7} color='default'>
      days
    </NumberTag>{' '}
    if they choose to connect
  </div>
);

const IntroReceivedHeader = ({ introduction }: { introduction: MyProjectsIntroduction }) => (
  <div>
    Take action before this introduction expires in{' '}
    <NumberTag
      size='sm'
      variant='badge'
      leadNumber={getDaysUntilExpiry(introduction)}
      color='accent'
    >
      days
    </NumberTag>
  </div>
);

export const getHeaderContent = ({
  cardData,
  userType,
}: { cardData: ProCard; userType: 'pro' } | { cardData: RepCard; userType: 'rep' }): {
  headerColor: string;
  headerText: ReactNode;
} => {
  const cardStatus = cardData.cardStatus;
  let headerText: ReactNode = '';
  let headerColor = '';
  const cardStatusChangedStr = formatDateStr(cardData.cardStatusChangedAt);

  switch (cardStatus) {
    case CardStatusEnum.intro_org_sent_pending:
      headerColor = userType === 'rep' ? 'gray-50' : 'warning-50';
      headerText =
        userType === 'rep' ? USER_SENT_INTRO_HEADER : (
          <IntroReceivedHeader introduction={cardData.introduction} />
        );
      break;
    case CardStatusEnum.intro_pro_sent_pending:
      headerColor = userType === 'pro' ? 'gray-50' : 'warning-50';
      headerText =
        userType === 'pro' ? USER_SENT_INTRO_HEADER : (
          <IntroReceivedHeader introduction={cardData.introduction} />
        );
      break;
    case CardStatusEnum.intro_org_sent_declined:
      headerColor = 'gray-100';
      headerText = `${userType === 'rep' ? 'This Experteer declined your' : 'You declined this'} introduction on ${cardStatusChangedStr}`;
      break;
    case CardStatusEnum.intro_pro_sent_declined:
      headerColor = 'gray-100';
      headerText = `${userType === 'pro' ? 'The organization declined your' : 'You declined this'} introduction on ${cardStatusChangedStr}`;
      break;
    case CardStatusEnum.intro_org_sent_expired:
      headerColor = 'gray-100';
      headerText = `${userType === 'rep' ? "This Experteer didn't respond in time, your introduction" : 'This introduction'} expired on ${cardStatusChangedStr}`;
      break;
    case CardStatusEnum.intro_pro_sent_expired:
      headerColor = 'gray-100';
      headerText = `${userType === 'pro' ? "This organization didn't respond in time, your introduction" : 'This introduction'} expired on ${cardStatusChangedStr}`;
      break;
    case CardStatusEnum.intro_org_sent_withdrawn:
      headerColor = 'gray-100';
      headerText = `${userType === 'pro' ? 'This organization withdrew their introduction' : 'This introduction was withdrawn'} on ${cardStatusChangedStr}`;
      break;
    case CardStatusEnum.intro_pro_sent_withdrawn:
      headerColor = 'gray-100';
      headerText = `${userType === 'rep' ? 'This Experteer withdrew their introduction' : 'This introduction was withdrawn'} on ${cardStatusChangedStr}`;
      break;
    case CardStatusEnum.intro_system_declined_other_pro:
      headerColor = 'gray-100';
      headerText = `${userType === 'pro' ? "This project is no longer available because it's been matched" : `Declined on ${cardStatusChangedStr} because you matched with a different Experteer`}`;
      break;
    case CardStatusEnum.intro_system_declined_other_project:
      headerColor = 'gray-100';
      headerText = `${userType === 'rep' ? "This Experteer is no longer available because they've been matched" : `Declined on ${cardStatusChangedStr} because you matched with a different project`}`;
      break;
    case CardStatusEnum.discovery_pending:
      headerColor = 'warning-50';
      headerText = "You're connected! See your Project Hub for next steps";
      break;
    case CardStatusEnum.discovery_org_accepted:
      headerColor = `${userType === 'pro' ? 'warning-50' : 'gray-100'}`;
      headerText =
        userType === 'pro' ?
          "You're connected! See your Project Hub for next steps"
          : `Match request sent! We'll let you know when they confirm`;
      break;
    case CardStatusEnum.discovery_org_declined:
      headerColor = 'gray-100';
      headerText = `${userType === 'pro' ? 'This organization declined to match' : 'You declined to match'} on ${cardStatusChangedStr}`;
      break;
    case CardStatusEnum.discovery_pro_declined:
      headerColor = 'gray-100';
      headerText = `${userType === 'pro' ? 'You declined to match' : 'This Experteer declined to match'} on ${cardStatusChangedStr}`;
      break;
    case CardStatusEnum.discovery_system_declined_other_pro:
      headerColor = 'gray-100';
      headerText = `${userType === 'pro' ? "This project is no longer available because it's been matched" : `Declined on ${cardStatusChangedStr} because you matched with a different Experteer`}`;
      break;
    case CardStatusEnum.discovery_system_declined_other_project:
      headerColor = 'gray-100';
      headerText = `${userType === 'rep' ? "This Experteer is no longer available because they've been matched" : `Declined on ${cardStatusChangedStr} because you matched with a different project`}`;
      break;
    case CardStatusEnum.project_prep_pending:
      headerColor = 'warning-50';
      headerText = 'Congrats on matching! See your Project Hub for next steps';
      break;
    case CardStatusEnum.project_work_pending:
      headerColor = 'warning-50';
      headerText = 'Your project is in progress! See your Project Hub for next steps';
      break;
    case CardStatusEnum.project_survey_pending:
      headerColor = 'success-25';
      headerText =
        "You're at the finish line! Complete your wrap-up — see your Project Hub for next steps.";
      break;
    case CardStatusEnum.project_active:
      headerColor = 'warning-50';
      headerText = 'Your project is in progress! See your Project Hub for next steps';
      break;
    case CardStatusEnum.project_complete:
      headerColor = 'success-25';
      headerText = `This project was completed ${cardStatusChangedStr}`;
      break;
    case CardStatusEnum.project_assigned_no_updates:
      headerColor = 'warning-50';
      headerText = 'This project was previously matched, but we don’t have any recent updates';
      break;
    default:
      throw new Error(`Invalid card status: ${cardStatus}`);
  }

  return {
    headerColor,
    headerText,
  };
};

export const getCardStatusBadge = (
  cardStatus: string,
): { text: string; color: PillBadgeColors } => {
  switch (cardStatus) {
    case CardStatusEnum.project_prep_pending:
    case CardStatusEnum.project_work_pending:
    case CardStatusEnum.project_active:
    case CardStatusEnum.project_assigned_no_updates:
      return {
        text: 'Matched',
        color: 'success-25',
      };
    case CardStatusEnum.project_survey_pending:
    case CardStatusEnum.project_complete:
      return {
        text: 'Completed',
        color: 'success-100',
      };
    case CardStatusEnum.discovery_pending:
    case CardStatusEnum.discovery_pro_declined:
    case CardStatusEnum.discovery_org_accepted:
    case CardStatusEnum.discovery_org_declined:
    case CardStatusEnum.discovery_system_declined_other_pro:
    case CardStatusEnum.discovery_system_declined_other_project:
      return {
        text: 'Connected',
        color: 'secondary-brand-50',
      };
    case CardStatusEnum.intro_pro_sent_pending:
    case CardStatusEnum.intro_pro_sent_declined:
    case CardStatusEnum.intro_pro_sent_expired:
    case CardStatusEnum.intro_pro_sent_withdrawn:
    case CardStatusEnum.intro_org_sent_pending:
    case CardStatusEnum.intro_org_sent_declined:
    case CardStatusEnum.intro_org_sent_expired:
    case CardStatusEnum.intro_org_sent_withdrawn:
    case CardStatusEnum.intro_system_declined_other_pro:
    case CardStatusEnum.intro_system_declined_other_project:
      return {
        text: 'Introduction',
        color: 'warning-100',
      };
    default:
      throw new Error(`Invalid card status: ${cardStatus}`);
  }
};

export const getButtonProps = ({
  openIntroModal,
  cardData,
  userType,
}:
  | {
    openIntroModal: () => void;
    cardData: ProCard;
    userType: 'pro';
  }
  | {
    openIntroModal: () => void;
    cardData: RepCard;
    userType: 'rep';
  }):
  | {
    buttonText: string;
    buttonVariant: ButtonVariants;
    buttonCta: () => void;
  }
  | undefined => {
  const { cardStatus } = cardData;
  const introSentByUser =
    (cardData.introduction.initiationType === 'Experteer' && userType === 'pro') ||
    (cardData.introduction.initiationType === 'Organization' && userType === 'rep');

  const navigateToProjectHub = () => {
    if (cardData.assignmentDiscoveryId) {
      navigate(`/platform/project-hub/${cardData.assignmentDiscoveryId}`);
    }
  };

  switch (cardStatus) {
    case CardStatusEnum.intro_org_sent_pending:
      return {
        buttonText: userType === 'rep' ? 'See My Introduction' : 'See Introduction',
        buttonVariant: userType === 'rep' ? 'secondary-gray' : 'primary',
        buttonCta: openIntroModal,
      };
    case CardStatusEnum.intro_pro_sent_pending:
      return {
        buttonText: userType === 'pro' ? 'See My Introduction' : 'See Introduction',
        buttonVariant: userType === 'pro' ? 'secondary-gray' : 'primary',
        buttonCta: openIntroModal,
      };
    case CardStatusEnum.intro_org_sent_expired:
    case CardStatusEnum.intro_org_sent_declined:
    case CardStatusEnum.intro_org_sent_withdrawn:
    case CardStatusEnum.intro_system_declined_other_project:
    case CardStatusEnum.intro_pro_sent_declined:
    case CardStatusEnum.intro_pro_sent_expired:
    case CardStatusEnum.intro_pro_sent_withdrawn:
    case CardStatusEnum.intro_system_declined_other_pro:
      return {
        buttonText: introSentByUser ? 'See My Introduction' : 'See Introduction',
        buttonVariant: 'secondary-gray',
        buttonCta: openIntroModal,
      };
    case CardStatusEnum.project_prep_pending:
    case CardStatusEnum.project_work_pending:
    case CardStatusEnum.project_survey_pending:
    case CardStatusEnum.project_active:
    case CardStatusEnum.discovery_pending:
    case CardStatusEnum.discovery_org_accepted:
    case CardStatusEnum.project_assigned_no_updates:
      return {
        buttonText: 'Project Hub',
        buttonVariant: 'primary',
        buttonCta: navigateToProjectHub,
      };

    case CardStatusEnum.discovery_system_declined_other_pro:
    case CardStatusEnum.discovery_org_declined:
    case CardStatusEnum.discovery_pro_declined:
    case CardStatusEnum.discovery_system_declined_other_project:
    case CardStatusEnum.project_complete:
      return {
        buttonText: 'Project Hub',
        buttonVariant: 'secondary-gray',
        buttonCta: navigateToProjectHub,
      };

    default:
      return undefined;
  }
};

export const getSubtext = ({
  cardData,
  userType,
}: { cardData: ProCard; userType: 'pro' } | { cardData: RepCard; userType: 'rep' }): string => {
  const getPrefix = () => {
    switch (cardData.cardStatus) {
      case CardStatusEnum.project_complete:
      case CardStatusEnum.project_survey_pending:
        return ['Completed', cardData.cardStatusChangedAt];
      case CardStatusEnum.project_prep_pending:
      case CardStatusEnum.project_work_pending:
      case CardStatusEnum.project_active:
      case CardStatusEnum.project_assigned_no_updates:
        return ['Matched', cardData.cardStatusChangedAt];
      case CardStatusEnum.discovery_pending:
      case CardStatusEnum.discovery_org_accepted:
      case CardStatusEnum.discovery_pro_declined:
      case CardStatusEnum.discovery_org_declined:
      case CardStatusEnum.discovery_system_declined_other_pro:
      case CardStatusEnum.discovery_system_declined_other_project:
        return ['Connected', cardData.cardStatusChangedAt];
      case CardStatusEnum.intro_pro_sent_declined:
      case CardStatusEnum.intro_pro_sent_expired:
      case CardStatusEnum.intro_pro_sent_pending:
      case CardStatusEnum.intro_pro_sent_withdrawn:
      case CardStatusEnum.intro_system_declined_other_pro:
        return [userType === 'pro' ? 'Sent' : 'Received', cardData.introduction.createdAt];
      case CardStatusEnum.intro_org_sent_declined:
      case CardStatusEnum.intro_org_sent_expired:
      case CardStatusEnum.intro_org_sent_pending:
      case CardStatusEnum.intro_org_sent_withdrawn:
      case CardStatusEnum.intro_system_declined_other_project:
        return [userType === 'pro' ? 'Received' : 'Sent', cardData.introduction.createdAt];

      default:
        throw new Error(
          `Unhandled card status when getting Subtext prefix, cardStatus: ${cardData.cardStatus}`,
        );
    }
  };

  const [prefix, dateString] = getPrefix();

  return `${prefix} ${formatDateStr(dateString)}`;
};

export const formatDateStr = (dateString: string) => {
  return new Date(dateString).toLocaleDateString('en-CA', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

export const getExpiryDate = (introduction: MyProjectsIntroduction) => {
  const introductionExpiresAt = new Date(introduction.createdAt);
  introductionExpiresAt.setDate(introductionExpiresAt.getDate() + 7);
  return introductionExpiresAt;
};

const getDaysUntilExpiry = (introduction: MyProjectsIntroduction) => {
  const expiryDate = getExpiryDate(introduction);
  const now = new Date();
  const timeDiff = Math.abs(now.getTime() - expiryDate.getTime()) / (1000 * 60 * 60 * 24);
  // default to max of 7 days.. some legacy intros were not expired  by cron
  return timeDiff <= 7 ? Math.floor(timeDiff) : 7;
};
